import type { DrawerContext } from './drawer-context.js'
import { ContextProvider } from '@lit/context'
import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { SlotController } from '../common/controllers/SlotController.js'

import componentStyle from '../common/styles/Component.css'

import { drawerContext } from './drawer-context.js'
import style from './Drawer.css'
import '../footer/Footer.js'

/**
 * Drawer is used to display context-sensitive actions and  information.
 * Drawer doesn’t block users from completing their task, like a modal would.
 *
 * @status new
 * @category layout
 * @slot header - Optional slot that holds a header for the drawer.
 * @slot - Default slot.
 * @slot footer - Optional slot that holds footer content for the drawer.
 *
 * @cssprop [--n-drawer-padding=var(--n-space-l)] - Controls the padding around the main area (the default slot), using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-drawer-background-color=var(--n-color-surface)] - Controls the background color of the drawer.
 * @cssprop [--n-drawer-border-color=var(--n-color-border)] - Controls the color of the drawer's borders.
 * @cssprop [--n-drawer-border-radius=0] - Controls the border radius of the drawer.
 * @cssprop [--n-drawer-border-width=0] - Controls the width of the drawer's borders on every edge. The inline-start edge defaults to `1px` to separate the drawer from adjacent content; set this to `0` to remove all borders.
 * @cssprop [--n-drawer-border-inline-start-width=1px] - Controls the width of the drawer's inline-start border. Falls back to `--n-drawer-border-width`, then `1px`.
 * @cssprop [--n-drawer-border-inline-end-width=var(--n-drawer-border-width)] - Controls the width of the drawer's inline-end border.
 * @cssprop [--n-drawer-border-block-start-width=var(--n-drawer-border-width)] - Controls the width of the drawer's block-start border.
 * @cssprop [--n-drawer-border-block-end-width=var(--n-drawer-border-width)] - Controls the width of the drawer's block-end border.
 *
 * @localization closeLabel - Accessible label for the default close button rendered by `nord-drawer-close`.
 */
@customElement('nord-drawer')
export default class Drawer extends LitElement {
  static styles = [componentStyle, style]

  private footerSlot = new SlotController(this, 'footer')
  private closeContext: DrawerContext = {
    requestClose: () => this.remove(),
  }

  private closeProvider = new ContextProvider(this, {
    context: drawerContext,
    initialValue: this.closeContext,
  })

  /**
   * Controls the padding of the drawer component.
   */
  @property({ reflect: true }) padding: 'l' | 'm' | 's' | 'none' = 'l'

  render() {
    return html`
      <div class="n-drawer">
        <slot name="header"></slot>

        <div class="n-drawer-main">
          <slot></slot>
        </div>

        <nord-footer ?hidden=${this.footerSlot.isEmpty}>
          <slot name=${this.footerSlot.slotName}></slot>
        </nord-footer>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-drawer': Drawer
  }
}
